import { useEffect, useState, useMemo } from 'react';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useAppSelector } from 'hooks/useRedux';
import { RootState } from 'store';
import { http } from 'middleware/apiMiddleware';
import { businessPlans } from 'constants/apiUrls';
import { FETCH_STATES } from 'enum/common';
import { Loader } from 'components/Common/Loader';
import Render from 'components/Common/Render';
import PromotionPlans from 'components/Home/PromotionPlans';
import { checkIsAnywhereCommunity } from 'utils/common';
import { toast } from 'react-hot-toast';
import useMixpanel from 'hooks/useMixpanel';
import ActivateOfferModal from 'components/Home/ActivateOfferModal';
import ModalComponent from 'components/Common/Modal';
import BusinessHeader from 'components/Home/BusinessHeader';

const ListBusiness = () => {
  const [selectedBusiness] = useLocalStorage('selectedBusiness', null);
  const businessState = useAppSelector((state: RootState) => state.businesses);
  const business: BusinessType = businessState?.business || selectedBusiness || null;
  const { trackEvent } = useMixpanel();

  const [plans, setPlans] = useState<{
    status: FETCH_STATES;
    data: BusinessPlanType[];
  }>({ status: FETCH_STATES.LOADING, data: [] });

  const [activateOfferModal, setActivateOfferModal] = useState<ActivateOfferModalType>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [updatingAmount, setUpdatingAmount] = useState(false);

  const isAnyWhereCommunity = checkIsAnywhereCommunity(business);

  const fetchPromotionPlans = async (businessUuid: string) => {
    try {
      setPlans({ status: FETCH_STATES.LOADING, data: [] });
      const { data, error } = await http.get(businessPlans(businessUuid));
      if (error) {
        setPlans({ status: FETCH_STATES.FAILED, data: [] });
      }
      setPlans({ status: FETCH_STATES.SUCECSS, data });
    } catch {
      setPlans({ status: FETCH_STATES.FAILED, data: [] });
    }
  };

  const activateOffer = (moduleId: string, topUpAmount: number, referralCode: string) => {
    setActivateOfferModal({
      moduleId,
      topupAmount: topUpAmount,
      businessId: business?.uuid,
      referralCode,
    });
  };

  const deActivateOffer = async (businessPlan: BusinessPlanType) => {
    try {
      trackEvent('deactivate_offer_button_Clicking', {
        planId: businessPlan?.uuid,
      });

      const { data, error } = await http.patch(
        `/business/${business?.uuid}/business-paid-modules/${businessPlan?.uuid}`,
        {
          enabled: false,
        },
      );
      if (!!data && !error) {
        toast.success('Plan deactivated successfully');
      }
      trackEvent('deactivate_offer_success', {
        planId: businessPlan?.uuid ?? '',
      });
      fetchPromotionPlans(business?.uuid);
    } catch (error) {
      trackEvent('deactivate_offer_failure', {
        planId: businessPlan?.uuid ?? '',
      });
      toast.error('Unexpected error occurred while deactivating plan');
    }
  };

  const updateTopupAmount = async (moduleId: string, topUpAmount: number) => {
    try {
      setUpdatingAmount(true);
      const { data, error } = await http.patch(
        `/business/${business?.uuid}/business-paid-modules/${moduleId}`,
        {
          enabled: true,
          formData: { autoTopUpAmount: topUpAmount },
        },
      );
      if (!!data && !error) {
        toast.success('Topup amount updated successfully');
        trackEvent('update_topup_amount_success', {
          planId: moduleId,
          topUpAmount,
        });
      }
    } catch (error) {
      trackEvent('update_topup_amount_failure', {
        planId: moduleId,
        topUpAmount,
        error,
      });
      toast.error('Unexpected error occurred while updating topup amount');
    } finally {
      setUpdatingAmount(false);
    }
  };

  const onCancelActivateOfferModal = () => {
    setActivateOfferModal(null);
    fetchPromotionPlans(business?.uuid);
  };

  const onCloseModal = () => {
    setActivateOfferModal(null);
  };

  useEffect(() => {
    if (business) {
      fetchPromotionPlans(business?.uuid);
    }
  }, [business?.uuid]);

  const listBusinessPlans = useMemo(
    () =>
      plans?.data?.filter((plan) => plan?.module?.label === 'List your business on Open Rewards') ||
      [],
    [plans?.data],
  );

  const isLoading = plans?.status === FETCH_STATES.LOADING;

  return (
    <>
      <div className="bg-white p-8 pt-6 h-full flex-col flex gap-5">
        <Render when={!!business}>
          <BusinessHeader selectedBusiness={business} />
          <Render when={isLoading}>
            <div className="flex justify-center items-center h-screen">
              <Loader size={8} color={'darkBlue'} />
            </div>
          </Render>
          <Render when={!isLoading}>
            <PromotionPlans
              activateOffer={activateOffer}
              deActivateOffer={deActivateOffer}
              plans={listBusinessPlans}
              updateTopupAmount={updateTopupAmount}
              isUpdatingAmount={updatingAmount}
              isAnyWhereCommunity={isAnyWhereCommunity}
              business={business}
              errorMessage={errorMessage}
              setErrorMessage={(value) => setErrorMessage(value)}
              activateInstantPay={() => {}}
              activateSubscription={() => {}}
              showRegisteredBusinessCard={true}
            />
          </Render>
        </Render>
      </div>
      <Render when={!!activateOfferModal}>
        <ModalComponent
          showClose
          containerClass="md:w-[50vw] md:h-auto h-auto max-h-[80vh] w-[80vw] !md:m-0 !p-0 !z-50"
          onClose={onCancelActivateOfferModal}
          renderHeader={
            <div className="text-darkBlue md:text-xl text-base font-medium">Activate Plan</div>
          }
        >
          <ActivateOfferModal
            activateOfferModal={activateOfferModal}
            onSuccess={onCancelActivateOfferModal}
            onClose={onCloseModal}
            setErrorMessage={(value) => setErrorMessage(value)}
          />
        </ModalComponent>
      </Render>
    </>
  );
};

export default ListBusiness;
